"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useReducedMotion } from "framer-motion";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { CONTACT_STEP_INTERVAL, contactSteps } from "@/data/ContactData";
import { rise } from "@/lib/animation/variant";
import { useAutoRotate } from "@/hooks/ui/useAutoRotate";
import { useProjectedDates } from "@/hooks/scheduling/useProjectedDates";
import ContactStepRow from "./ContactStepRow";
import ContactTimelineCard from "./ContactTimelineCard";
const WhatHappensNext = () => {
  const $ = _c(29);
  const reduceMotion = useReducedMotion();
  const {
    active,
    select,
    running,
    hoverProps
  } = useAutoRotate(contactSteps.length, CONTACT_STEP_INTERVAL, !reduceMotion);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = contactSteps.map(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dates = useProjectedDates(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden={true} className="pointer-events-none absolute top-0 left-0 hidden select-none text-[16vw] font-black uppercase leading-none tracking-tighter text-black/4 lg:block">Next</span>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      once: true,
      amount: 0.5
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SectionSubHeading subtitle="After you hit send" className="text-neutral-500" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <motion.div variants={rise} initial="hidden" whileInView="show" viewport={t2} className="max-w-2xl space-y-2">{t3}<SectionTitle as="h1" title={<>WHAT HAPPENS <br /> NEXT.</>} className="font-black leading-tight text-neutral-900" /></motion.div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <motion.p variants={rise} initial="hidden" whileInView="show" viewport={{
      once: true,
      amount: 0.5
    }} custom={1} className="mt-5 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">No black box and no chasing. Four stages, each one landing on a day you can put in your own calendar.</motion.p>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      once: true,
      amount: 0.2
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = (active + 1) / contactSteps.length;
  let t8;
  if ($[7] !== t7) {
    t8 = {
      scaleY: t7
    };
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      type: "spring",
      stiffness: 120,
      damping: 24
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t8) {
    t10 = <span aria-hidden={true} className="absolute bottom-10 left-5 top-10 w-px -translate-x-1/2 bg-black/10 md:left-6"><motion.span animate={t8} transition={t9} className="block h-full w-full origin-top bg-primary" /></span>;
    $[10] = t8;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== active || $[13] !== running || $[14] !== select) {
    t11 = contactSteps.map((step, i) => <ContactStepRow key={step.number} step={step} index={i} active={i === active} running={running && i === active} intervalMs={CONTACT_STEP_INTERVAL} onSelect={select} />);
    $[12] = active;
    $[13] = running;
    $[14] = select;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11) {
    t12 = <div className="relative divide-y divide-white/5">{t11}</div>;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t10 || $[19] !== t12) {
    t13 = <div className="relative">{t10}{t12}</div>;
    $[18] = t10;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== active || $[22] !== dates || $[23] !== select) {
    t14 = <ContactTimelineCard steps={contactSteps} dates={dates} active={active} onSelect={select} />;
    $[21] = active;
    $[22] = dates;
    $[23] = select;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== hoverProps || $[26] !== t13 || $[27] !== t14) {
    t15 = <section className="relative w-full overflow-hidden bg-[#f4f6ec] py-20 md:py-24 lg:py-28">{t1}<Container className="relative px-4 lg:px-10">{t4}{t5}<motion.div variants={rise} initial="hidden" whileInView="show" viewport={t6} custom={2} {...hoverProps} className="mt-14 grid grid-cols-1 items-start gap-10 lg:mt-20 lg:grid-cols-2 lg:gap-16">{t13}{t14}</motion.div></Container></section>;
    $[25] = hoverProps;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  return t15;
};
export default WhatHappensNext;
function _temp(s) {
  return s.offsetDays;
}